<template>
  <div class="ap-card p-0 overflow-hidden">
    <div class="card-header-styled">
      <i class="fas fa-id-card me-1"></i>Patient Details
    </div>
    <div class="p-3 pt-2">
      <div class="row g-2">
        <div class="col-sm-6">
          <label class="ap-label">Full Name <span class="req">*</span></label>
          <div class="input-group input-group-merge" :class="{ 'has-error': errors.name }">
            <span class="input-group-text"><i class="fas fa-user text-muted"></i></span>
            <input
              :value="modelValue.name"
              @input="updateField('name', $event.target.value)"
              type="text"
              class="form-control ap-inp"
              placeholder="Patient's full name"
            />
          </div>
          <span v-if="errors.name" class="err-msg">{{ errors.name }}</span>
        </div>

        <div class="col-sm-6">
          <label class="ap-label">Mobile Number <span class="req">*</span></label>
          <div class="input-group input-group-merge" :class="{ 'has-error': errors.mobile }">
            <span class="input-group-text text-muted">+880</span>
            <input
              :value="modelValue.mobile"
              @input="updateField('mobile', $event.target.value)"
              type="tel"
              class="form-control ap-inp"
              placeholder="01X-XXXX-XXXX"
            />
          </div>
          <span v-if="errors.mobile" class="err-msg">{{ errors.mobile }}</span>
        </div>

        <div class="col-sm-6">
          <label class="ap-label">Email Address <span v-if="isSignupRequired" class="req">*</span><span v-else class="optional">(optional)</span></label>
          <div class="input-group input-group-merge" :class="{ 'has-error': errors.email }">
            <span class="input-group-text"><i class="fas fa-envelope text-muted"></i></span>
            <input
              :value="modelValue.email"
              @input="updateField('email', $event.target.value)"
              type="email"
              class="form-control ap-inp"
              placeholder="example@mail.com"
            />
          </div>
          <span v-if="errors.email" class="err-msg">{{ errors.email }}</span>
        </div>

        <div class="col-sm-6">
          <label class="ap-label">Age</label>
          <div class="age-row">
            <div class="age-f">
              <span class="age-tag">Yr</span>
              <input
                :value="modelValue.age_year"
                @input="updateField('age_year', $event.target.value)"
                type="number"
                class="form-control ap-inp"
                placeholder="0"
                min="0"
                max="120"
              />
            </div>
            <div class="age-f">
              <span class="age-tag">Mo</span>
              <input
                :value="modelValue.age_month"
                @input="updateField('age_month', $event.target.value)"
                type="number"
                class="form-control ap-inp"
                placeholder="0"
                min="0"
                max="11"
              />
            </div>
            <div class="age-f">
              <span class="age-tag">Day</span>
              <input
                :value="modelValue.age_day"
                @input="updateField('age_day', $event.target.value)"
                type="number"
                class="form-control ap-inp"
                placeholder="0"
                min="0"
                max="30"
              />
            </div>
          </div>
        </div>

        <div class="col-sm-6">
          <label class="ap-label">Date of Birth</label>
          <input
            :value="modelValue.dob"
            @input="updateField('dob', $event.target.value)"
            type="date"
            class="form-control ap-inp dob-inp"
          />
        </div>

        <div class="col-sm-6">
          <label class="ap-label">Gender <span class="req">*</span></label>
          <div class="d-flex gap-2">
            <label
              v-for="g in genderOptions"
              :key="g.value"
              class="gender-lbl"
              :class="{ 'g-active': modelValue.gender === g.value, 'has-error': errors.gender }"
            >
              <input
                type="radio"
                :value="g.value"
                :checked="modelValue.gender === g.value"
                @change="updateField('gender', g.value)"
                class="d-none"
              />
              <i :class="g.icon"></i>
              <span>{{ g.label }}</span>
              <i class="fas fa-check-circle ms-1 sel-check"></i>
            </label>
          </div>
          <span v-if="errors.gender" class="err-msg">{{ errors.gender }}</span>
        </div>

        <div class="col-12">
          <label class="ap-label">Full Address <span class="optional">(optional)</span></label>
          <div class="input-group input-group-merge" :class="{ 'has-error': errors.address }">
            <span class="input-group-text pt-2 align-items-start"
              ><i class="fas fa-map-marker-alt text-muted"></i
            ></span>
            <textarea
              :value="modelValue.address"
              @input="updateField('address', $event.target.value)"
              class="form-control ap-inp"
              rows="2"
              placeholder="House, Road, Area, District"
            ></textarea>
          </div>
          <span v-if="errors.address" class="err-msg">{{ errors.address }}</span>
        </div>

        <template v-if="isSignupRequired">
          <div class="col-sm-6">
            <label class="ap-label">Password <span class="req">*</span></label>
            <div class="input-group input-group-merge" :class="{ 'has-error': errors.password }">
              <span class="input-group-text"><i class="fas fa-lock text-muted"></i></span>
              <input
                :value="modelValue.password"
                @input="updateField('password', $event.target.value)"
                type="password"
                class="form-control ap-inp"
                placeholder="············"
              />
            </div>
            <span v-if="errors.password" class="err-msg">{{ errors.password }}</span>
          </div>

          <div class="col-sm-6">
            <label class="ap-label">Confirm Password <span class="req">*</span></label>
            <div
              class="input-group input-group-merge"
              :class="{ 'has-error': errors.password_confirmation }"
            >
              <span class="input-group-text"><i class="fas fa-lock text-muted"></i></span>
              <input
                :value="modelValue.password_confirmation"
                @input="updateField('password_confirmation', $event.target.value)"
                type="password"
                class="form-control ap-inp"
                placeholder="············"
              />
            </div>
            <span v-if="errors.password_confirmation" class="err-msg">{{
              errors.password_confirmation
            }}</span>
          </div>
        </template>
      </div>

      <div class="form-footer mt-3">
        <div class="d-flex flex-column">
          <span class="secure-note">
            <i class="fas fa-shield-alt text-success me-1"></i>Your data is secure &amp; confidential.
          </span>
          <span v-if="Object.keys(errors).length > 0" class="err-msg mt-1">
            <i class="fas fa-exclamation-triangle me-1"></i>Please fix the errors above to continue.
          </span>
        </div>
        <button type="button" class="btn-book" @click="$emit('submit')">
          <i class="fas fa-calendar-check me-2"></i>Confirm Appointment
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
  genderOptions: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue', 'submit']);

const registrationType = computed(() => {
  return store.state.dental.company ? store.state.dental.company.registration_type : 'with_signup';
});

const isSignupRequired = computed(() => {
  return registrationType.value === 'with_signup';
});

const updateField = (field, value) => {
  const updatedValue = { ...props.modelValue };
  updatedValue[field] = value;
  
  if (['age_year', 'age_month', 'age_day'].includes(field) && value !== '') {
    updatedValue[field] = Number(value);
  }
  
  emit('update:modelValue', updatedValue);
};
</script>

<style scoped>
.ap-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 3px 16px rgba(115, 103, 240, 0.1);
}

.card-header-styled {
  background: #f8f8f8;
  padding: 12px 20px;
  border-bottom: 1px solid #e4e1f0;
  font-size: 1rem;
  font-weight: 700;
  color: #2d2d3a;
  display: flex;
  align-items: center;
}
.card-header-styled i {
  color: #7367f0;
  font-size: 1.1rem;
}

.ap-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.optional {
  font-size: 0.72rem;
  color: #6e6b7b;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}
.req {
  color: #e53e3e;
}

.input-group-merge {
  border: 1.5px solid #e4e1f0;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;
  background: #fff;
  overflow: hidden;
  display: flex;
}
.input-group-merge:focus-within {
  border-color: #7367f0;
  box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.15);
}
.input-group-merge.has-error {
  border-color: #ea5455;
}

.input-group-merge .input-group-text {
  background: transparent !important;
  border: none !important;
  padding-left: 12px;
  padding-right: 8px;
  font-size: 0.88rem;
  display: flex;
  align-items: center;
}

.ap-inp {
  border: 1.5px solid #e4e1f0 !important;
  border-radius: 6px !important;
  font-size: 0.88rem !important;
  padding: 8px 12px !important;
  color: #2d2d3a !important;
  background: #fff !important;
  transition: all 0.2s ease-in-out !important;
  flex: 1;
}
.ap-inp:focus {
  border-color: #7367f0 !important;
  box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.15) !important;
  outline: none !important;
}

.input-group-merge .ap-inp {
  border: none !important;
  border-radius: 0 !important;
  padding-left: 4px !important;
  box-shadow: none !important;
  background: transparent !important;
}

.age-row {
  display: flex;
  gap: 6px;
}
.age-f {
  flex: 1;
  position: relative;
}
.age-tag {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  font-size: 0.62rem;
  font-weight: 700;
  color: #6e6b7b;
  text-transform: uppercase;
  pointer-events: none;
  background: #f0f0f0;
  padding: 2px 4px;
  border-radius: 4px;
}

.gender-lbl {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1.5px solid #e4e1f0;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  transition: all 0.18s;
  user-select: none;
}
.gender-lbl:hover {
  border-color: #7367f0;
  background: #ede9fd;
  color: #7367f0;
}
.gender-lbl.g-active {
  border-color: #7367f0;
  background: #ede9fd;
  color: #7367f0;
  box-shadow: 0 0 0 1px #7367f0;
}
.gender-lbl.has-error {
  border-color: #ea5455;
}

.sel-check {
  font-size: 0.8rem;
  display: none;
}
.g-active .sel-check {
  display: inline-block;
  animation: scaleIn 0.2s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.err-msg {
  display: block;
  font-size: 0.75rem;
  color: #ea5455;
  margin-top: 4px;
  font-weight: 500;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  border-top: 1.5px dashed #e4e1f0;
  padding-top: 14px;
}
.secure-note {
  font-size: 0.8rem;
  color: #6e6b7b;
}

.btn-book {
  background: linear-gradient(135deg, #7367f0 0%, #9186f3 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 36px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.4);
}
.btn-book:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(115, 103, 240, 0.45);
}
.btn-book:active {
  transform: translateY(0);
}

@media (max-width: 576px) {
  .age-row {
    flex-wrap: wrap;
  }
}
</style>
